import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterService } from '../../services/filter.service';
import { MatTableDataSource } from '@angular/material/table';
import { DateRange, Transaction } from '@contracts/interfaces/transaction.interfaces';
import { TransactionsService } from '../../services/transactions.service';
import { AccountService } from '../../services/account.service';
import { ACCOUNT } from 'src/app/accounts';
import { forkJoin } from 'rxjs';
import { curateTransactionDescription } from 'src/app/tools/curate-transaction-description';
import { AppSettingsService } from 'src/app/services/app-settings.service';
import { TransactionStatementSummaryService } from 'src/app/services/transaction-statement-summary.service';
import { BankStatementSummary, BankStatementTransactionRow, TransactionStatementPeriodSummary } from '@contracts/interfaces/transaction-statement-summary.interface';
import { FinancialYearAttachment } from '@contracts/interfaces/financial-year-summary.interface';

enum DateMode {
  Weekly,
  Monthly,
  Quaterly,
  Yearly,
  AllTime,
  Custom
}

interface StatementSummaryRow extends BankStatementSummary {
  currentCredits: number;
  currentDebits: number;
  currentCount: number;
}

interface MissingStatementTransaction extends BankStatementTransactionRow {
  sha1: string;
  isAdding?: boolean;
}

type TransactionDirectionFilter = 'all' | 'debits' | 'credits';

@Component({
  selector: 'app-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransactionsComponent implements OnInit, OnDestroy {
  dataSource: MatTableDataSource<Transaction> = new MatTableDataSource<Transaction>([]);
  columnsToDisplay = [
    'date',
    'description', 
    'type',
    'debit',
    'credit',
    'balance',
  ];
  readonly summaryTab = 'summary';
  private readonly dateRangeStorageKey = 'transactionsDateRange';
  DateMode = DateMode;
  dateMode: DateMode = DateMode.Weekly;
  dateOffset = 0;
  dateRange: DateRange = null;
  customDateRange: any = null;
  transactionDirectionFilter: TransactionDirectionFilter = 'all';
  statementSummary: TransactionStatementPeriodSummary | null = null;
  statementRows: StatementSummaryRow[] = [];
  missingStatementTransactions: MissingStatementTransaction[] = [];
  isLoadingStatementSummary = false;
  isSavingStatementSummary = false;
  statementSaveMessage = '';
  uploadingStatementAccount = '';
  private statementAutosaveTimer: any = null;

  constructor(public transactionsService: TransactionsService,
              public accountService: AccountService,
              public filterService: FilterService,
              private appSettingsService: AppSettingsService,
              private transactionStatementSummaryService: TransactionStatementSummaryService,
              private cdr: ChangeDetectorRef,
              private router: Router,
              private route: ActivatedRoute,) { }

  accounts: any[] = [];
  activeAccount = "";

  info: any = {
    credits: 0,
    debits: 0,
    count: 0,
    salesamount: 0,
    vat: 0
  };

  ngOnInit(): void {
    this.dateRange = this.getDateRange();

    this.accountService.find({}).subscribe((accounts: any[]) => {
      this.accounts = accounts;
      this.cdr.markForCheck();
    });

    this.route.params.subscribe(params => {
      const account = params.account || ACCOUNT.G72REVENUE;

      if (!params.account) {
        this.switchCategory(account);
        return;
      }

      this.activeAccount = account;
      this.loadItems(account);
    });

    this.filterService.onChange(() => {
      this.refresh();
    });
  }

  ngOnDestroy(): void {
    this.filterService.onChangeClear();
    if (this.statementAutosaveTimer) {
      clearTimeout(this.statementAutosaveTimer);
    }
  }

  switchCategory(account: string) {
    this.router.navigate(["transactions/" + account]);
  }

  loadItems(account: string) {
    if (!account) {
      return;
    }

    if (account === this.summaryTab) {
      this.loadSummary();
      return;
    }

    const query = this.buildQuery(account);

    forkJoin({
      transactions: this.transactionsService.find(query, null, null, { date: -1 }),
      info: this.transactionsService.infoData(query),
      statementSummary: this.transactionStatementSummaryService.get(this.dateRange)
    }).subscribe(({ transactions, info, statementSummary }) => {
      this.statementSummary = statementSummary;
      this.dataSource = new MatTableDataSource<Transaction>(this.decorateTransactions(transactions, account, statementSummary));
      this.missingStatementTransactions = this.getMissingStatementTransactions(account, transactions, statementSummary);
      this.info = info;
      this.cdr.markForCheck();
    });
  }

  refresh() {
    this.loadItems(this.activeAccount);
  }

  setTransactionDirectionFilter(filter: TransactionDirectionFilter) {
    this.transactionDirectionFilter = filter;
    this.refresh();
  }

  getTransactionDirectionFilterLabel() {
    switch (this.transactionDirectionFilter) {
      case 'debits':
        return 'Debits';
      case 'credits':
        return 'Credits';
      default:
        return 'All transactions';
    }
  }

  loadSummary() {
    this.activeAccount = this.summaryTab;
    const query = this.buildDateRangeQuery();
    this.isLoadingStatementSummary = true;

    forkJoin({
      info: this.transactionsService.infoData(query),
      accounts: this.accountService.find({}),
      statementSummary: this.transactionStatementSummaryService.get(this.dateRange)
    }).subscribe(({ info, accounts, statementSummary }) => {
      const bankAccounts = (accounts || []) as any[];
      const accountInfoRequests = bankAccounts.map((account: any) => {
        const accountQuery = {
          ...query,
          date: { ...(query["date"] || {}) },
          ["source.account"]: account.accountNumber
        };

        return this.transactionsService.infoData(accountQuery);
      });

      this.info = info;
      this.statementSummary = statementSummary || this.createStatementSummary();

      if (accountInfoRequests.length === 0) {
        this.accounts = [];
        this.statementRows = [];
        this.dataSource = new MatTableDataSource<Transaction>([]);
        this.isLoadingStatementSummary = false;
        this.cdr.markForCheck();
        return;
      }

      forkJoin(accountInfoRequests).subscribe((accountInfos: any[]) => {
        this.accounts = bankAccounts;
        this.statementRows = this.buildStatementRows(this.accounts, accountInfos, this.statementSummary);
        this.dataSource = new MatTableDataSource<Transaction>([]);
        this.isLoadingStatementSummary = false;
        this.cdr.markForCheck();
      });
    });
  }

  updateStatementAmount(row: StatementSummaryRow, field: 'totalCreditsReported' | 'totalDebitsReported', amount: number) {
    row[field] = amount || 0;
    this.queueStatementSummarySave();
  }

  queueStatementSummarySave() {
    if (this.statementAutosaveTimer) {
      clearTimeout(this.statementAutosaveTimer);
    }

    this.statementSaveMessage = 'Saving...';
    this.statementAutosaveTimer = setTimeout(() => this.saveStatementSummary(), 600);
  }

  saveStatementSummary() {
    if (!this.dateRange || this.isSavingStatementSummary) {
      return;
    }

    const summary: TransactionStatementPeriodSummary = {
      ...(this.statementSummary || this.createStatementSummary()),
      startDate: this.dateRange.start,
      endDate: this.dateRange.end,
      statements: this.statementRows.map(row => ({
        accountNumber: row.accountNumber,
        accountName: row.accountName,
        totalCreditsReported: row.totalCreditsReported || 0,
        totalDebitsReported: row.totalDebitsReported || 0,
        transactions: row.transactions || [],
        statement: row.statement
      }))
    };

    this.isSavingStatementSummary = true;
    this.transactionStatementSummaryService.update(summary).subscribe({
      next: (updated) => {
        this.statementSummary = updated;
        this.statementRows = this.mergeSavedStatements(this.statementRows, updated);
        this.isSavingStatementSummary = false;
        this.statementSaveMessage = 'Statement totals saved.';
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSavingStatementSummary = false;
        this.statementSaveMessage = 'Could not save statement totals.';
        this.cdr.markForCheck();
      }
    });
  }

  uploadStatementCsv(event: Event, row: StatementSummaryRow) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !this.dateRange) {
      return;
    }

    this.uploadingStatementAccount = row.accountNumber;
    this.transactionStatementSummaryService.uploadStatementCsv(this.dateRange, row.accountNumber, row.accountName || row.accountNumber, file).subscribe({
      next: (summary) => {
        this.statementSummary = summary;
        this.statementRows = this.mergeSavedStatements(this.statementRows, summary);
        this.uploadingStatementAccount = '';
        input.value = '';
        this.cdr.markForCheck();
      },
      error: () => {
        this.uploadingStatementAccount = '';
        input.value = '';
        this.statementSaveMessage = 'Could not upload statement CSV.';
        this.cdr.markForCheck();
      }
    });
  }

  getStatementUrl(statement: FinancialYearAttachment) {
    return this.transactionStatementSummaryService.getStatementURL(statement);
  }

  getDebitVariance(row: StatementSummaryRow) {
    return Math.abs(row.currentDebits || 0) - (row.totalDebitsReported || 0);
  }

  getCreditVariance(row: StatementSummaryRow) {
    return (row.currentCredits || 0) - (row.totalCreditsReported || 0);
  }

  isStatementRowMatched(row: StatementSummaryRow) {
    return this.getDebitVariance(row) === 0 && this.getCreditVariance(row) === 0;
  }

  addMissingStatementTransaction(row: MissingStatementTransaction) {
    if (!this.activeAccount || row.isAdding) {
      return;
    }

    row.isAdding = true;
    const transaction = this.createTransactionFromStatementRow(row);

    this.transactionsService.insert(transaction as Transaction).subscribe(() => {
      this.refresh();
    }, () => {
      row.isAdding = false;
      this.cdr.markForCheck();
    });
  }

  getStatementRowDate(row: MissingStatementTransaction) {
    return this.parseBankDate(row.date);
  }

  openProofViewer(transaction: Transaction, index: number, dataSource: any) {
    this.transactionsService.openTransactionDialog(this.transactionsService.getTransactionType(transaction), transaction.source.sha1, index, dataSource, () => {
      this.refresh();
    });
  }

  getLabel() {
    switch (this.dateMode) {
      case DateMode.Weekly:
        return ["Previous Week", "Next Week"];
      case DateMode.Monthly:
        return ["Previous Month", "Next Month"];
      case DateMode.Quaterly:
        return ["Previous Quater", "Next Quater"];
      case DateMode.Yearly:
        return ["Previous Year", "Next Year"];
      case DateMode.AllTime:
        return ["", ""];
      case DateMode.Custom:
        return null;
    }
  }

  switchToCustomDateRange(range: any) {
    this.dateMode = DateMode.Custom;
    this.customDateRange = range;
    this.dateRange = {
      start: range.from,
      end: range.to
    };

    this.refresh();
  }

  cancelCustom() {
    this.switchToWeekly();
  }

  nextDateRange() {
    if (this.dateOffset > 0) {
      this.dateOffset--;
      this.findDateRange(this.dateMode, this.dateOffset);
      this.dateRange = this.getDateRange();
      this.refresh();
    }
  }

  previousDateRange() {
    if (this.isPreviousDateRangeDisabled()) {
      return;
    }

    this.dateOffset++;
    this.findDateRange(this.dateMode, this.dateOffset);
    this.dateRange = this.getDateRange();
    this.refresh();
  }

  getDateRange() {
    const dateRange = localStorage.getItem(this.dateRangeStorageKey);

    if (dateRange == null) {
      this.findDateRange(DateMode.Weekly, 0);
    }

    const temp = JSON.parse(localStorage.getItem(this.dateRangeStorageKey));

    this.dateOffset = temp.offset == null ? 0 : +temp.offset;
    this.dateMode = temp.dateMode == null ? 0 : +temp.dateMode;

    const range = this.clampDateRangeToBookkeepingStart(
      new Date(temp.start),
      new Date(temp.end)
    );

    return {
      ...range
    };
  }

  isPreviousDateRangeDisabled() {
    const bookkeepingStartDate = this.getBookkeepingStartDate();

    if (!bookkeepingStartDate || !this.dateRange) {
      return false;
    }

    if (this.dateMode === DateMode.AllTime) {
      return true;
    }

    const currentStart = new Date(this.dateRange.start);
    currentStart.setHours(0, 0, 0, 0);

    return currentStart <= bookkeepingStartDate;
  }

  setDateRange(startDate: Date, endDate: Date, dateMode: DateMode, offset: number) {
    const range = this.clampDateRangeToBookkeepingStart(startDate, endDate);

    localStorage.setItem(this.dateRangeStorageKey, JSON.stringify({
      start: range.start,
      end: range.end,
      dateMode: dateMode,
      offset: offset
    }));
  }

  findDateRange(dateMode: DateMode, dateOffset: number) {
    switch (dateMode) {
      case DateMode.Weekly:
        this.getWeekRange(dateOffset);
        break;
      case DateMode.Monthly:
        this.getMonthlyRange(dateOffset);
        break;
      case DateMode.Quaterly:
        this.getQuaterlyRange(dateOffset);
        break;
      case DateMode.Yearly:
        this.getYearlyRange(dateOffset);
        break;
      case DateMode.AllTime:
        this.getAllTimeRange();
    }
  }

  getMonthlyRange(monthOffset: number) {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() - monthOffset;

    const targetYear = year + Math.floor(month / 12);
    const targetMonth = (month % 12 + 12) % 12;
    const numDays = new Date(targetYear, targetMonth + 1, 0).getDate();

    const startDate = new Date(targetYear, targetMonth, 1);
    const endDate = new Date(targetYear, targetMonth, numDays);

    this.setDateRange(startDate, endDate, DateMode.Monthly, monthOffset);
  }

  getYearlyRange(yearOffset: number) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const targetYear = currentYear - yearOffset;

    if (currentDate.getMonth() > 6) {
      this.setDateRange(
        new Date(targetYear, 6, 1),
        new Date(targetYear + 1, 5, 30),
        DateMode.Yearly,
        yearOffset
      );
    } else {
      this.setDateRange(
        new Date(targetYear - 1, 6, 1),
        new Date(targetYear, 5, 30),
        DateMode.Yearly,
        yearOffset
      );
    }
  }

  getQuaterlyRange(dateOffset = 0) {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();

    month -= dateOffset * 3;

    while (month < 0) {
      month += 12;
      year--;
    }

    let quarter;
    let quarterStartMonth;

    if (month >= 6 && month <= 8) {
      quarter = 1;
      quarterStartMonth = 6;
    } else if (month >= 9 && month <= 11) {
      quarter = 2;
      quarterStartMonth = 9;
    } else if (month >= 0 && month <= 2) {
      quarter = 3;
      quarterStartMonth = 0;
    } else {
      quarter = 4;
      quarterStartMonth = 3;
    }

    const startDate = new Date(year, quarterStartMonth, 1);
    let endDate;

    if (quarter === 1 || quarter === 2) {
      endDate = new Date(year, quarterStartMonth + 2, quarter === 1 ? 30 : 31);
    } else if (quarter === 3) {
      endDate = new Date(year, 2, 31);
    } else {
      endDate = new Date(year, 5, 30);
    }

    endDate.setHours(23, 59, 59, 999);

    this.setDateRange(startDate, endDate, DateMode.Quaterly, dateOffset);
  }

  getWeekRange(weekOffset: number) {
    const date = new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 7 * weekOffset));
    const first = setToMonday(date);
    first.setHours(0, 0, 0, 0);

    const last = new Date(first.getTime());
    last.setDate(first.getDate() + 6);
    last.setHours(23, 59, 59);

    this.setDateRange(first, last, DateMode.Weekly, weekOffset);
  }

  getAllTimeRange() {
    this.setDateRange(
      new Date("1970-01-01"),
      new Date(),
      DateMode.AllTime,
      0
    );
  }

  switchToWeekly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Weekly;
    this.findDateRange(DateMode.Weekly, this.dateOffset);
    this.dateRange = this.getDateRange();
    this.refresh();
  }

  switchToMonthly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Monthly;
    this.findDateRange(DateMode.Monthly, this.dateOffset);
    this.dateRange = this.getDateRange();
    this.refresh();
  }

  switchToQuaterly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Quaterly;
    this.findDateRange(DateMode.Quaterly, this.dateOffset);
    this.dateRange = this.getDateRange();
    this.refresh();
  }

  switchToYearly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Yearly;
    this.findDateRange(DateMode.Yearly, this.dateOffset);
    this.dateRange = this.getDateRange();
    this.refresh();
  }

  switchToAllTime() {
    this.dateOffset = 0;
    this.dateMode = DateMode.AllTime;
    this.findDateRange(DateMode.AllTime, this.dateOffset);
    this.dateRange = this.getDateRange();
    this.refresh();
  }

  private buildQuery(account: string) {
    const query = this.buildDateRangeQuery();

    query["source.account"] = account;
    return query;
  }

  private buildDateRangeQuery() {
    const query = { ...(this.filterService.query || {}) };
    this.dateRange = this.dateRange || this.getDateRange();

    query["date"] = {
      $gte: this.dateRange.start,
      $lte: this.dateRange.end
    };

    if (this.transactionDirectionFilter === 'debits') {
      query["amount"] = { $lt: 0 };
    }

    if (this.transactionDirectionFilter === 'credits') {
      query["amount"] = { $gt: 0 };
    }

    return query;
  }

  private decorateTransactions(transactions: Transaction[], account?: string, statementSummary?: TransactionStatementPeriodSummary | null) {
    const uploadedTransactionCounts = this.getUploadedStatementTransactionCounts(account, statementSummary);
    const hasUploadedTransactions = uploadedTransactionCounts.size > 0;

    return (transactions || []).map(transaction => ({
      ...transaction,
      additional: this.decorateTransactionAdditional(transaction, uploadedTransactionCounts, hasUploadedTransactions)
    }));
  }

  private decorateTransactionAdditional(transaction: Transaction, uploadedTransactionCounts: Map<string, number>, hasUploadedTransactions: boolean) {
    const key = this.getTransactionMatchKey(transaction);
    const remainingMatches = uploadedTransactionCounts.get(key) || 0;
    const existsInUploadedStatement = remainingMatches > 0;

    if (existsInUploadedStatement) {
      uploadedTransactionCounts.set(key, remainingMatches - 1);
    }

    return {
      ...(transaction.additional || {}),
      curatedDescription: curateTransactionDescription(transaction.description),
      bankType: this.getBankTransactionType(transaction),
      bankBalance: this.getBankBalance(transaction),
      missingFromUploadedStatement: hasUploadedTransactions && !existsInUploadedStatement
    };
  }

  private getBankTransactionType(transaction: Transaction): string {
    const type = curateTransactionDescription(transaction.description).details[0];
    if (type === 'Deposit') return 'Deposits';
    if (type === 'Bank Payment') return 'Withdrawals & Transfers';
    return type || (transaction.amount < 0 ? 'Withdrawals & Transfers' : 'Deposits');
  }

  private getBankBalance(transaction: Transaction): number | null {
    const original = transaction.source?.original;
    const value = original?.Balance ?? original?.balance;
    if (value == null || String(value).trim() === '') return null;
    const balance = Number(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(balance) ? Math.round(balance * 100) : null;
  }

  private createStatementSummary(): TransactionStatementPeriodSummary {
    return {
      rangeKey: this.getRangeKey(this.dateRange.start, this.dateRange.end),
      startDate: this.dateRange.start,
      endDate: this.dateRange.end,
      statements: []
    };
  }

  private buildStatementRows(accounts: any[], accountInfos: any[], summary: TransactionStatementPeriodSummary | null): StatementSummaryRow[] {
    const savedStatements = summary?.statements || [];

    return (accounts || []).map((account, index) => {
      const savedStatement = savedStatements.find(statement => statement.accountNumber === account.accountNumber);
      const currentInfo = accountInfos[index] || {};

      return {
        accountNumber: account.accountNumber,
        accountName: account.name || account.accountNumber,
        totalCreditsReported: savedStatement?.totalCreditsReported ?? 0,
        totalDebitsReported: savedStatement?.totalDebitsReported ?? 0,
        transactions: savedStatement?.transactions || [],
        statement: savedStatement?.statement,
        currentCredits: currentInfo.credits || 0,
        currentDebits: currentInfo.debits || 0,
        currentCount: currentInfo.count || 0
      };
    });
  }

  private mergeSavedStatements(rows: StatementSummaryRow[], summary: TransactionStatementPeriodSummary): StatementSummaryRow[] {
    return rows.map(row => {
      const savedStatement = summary.statements?.find(statement => statement.accountNumber === row.accountNumber);

      return {
        ...row,
        totalCreditsReported: savedStatement?.totalCreditsReported ?? row.totalCreditsReported ?? 0,
        totalDebitsReported: savedStatement?.totalDebitsReported ?? row.totalDebitsReported ?? 0,
        transactions: savedStatement?.transactions || row.transactions || [],
        statement: savedStatement?.statement || row.statement
      };
    });
  }

  private getUploadedStatementTransactionCounts(account: string | undefined, statementSummary: TransactionStatementPeriodSummary | null | undefined) {
    const statement = statementSummary?.statements?.find(row => row.accountNumber === account);
    const counts = new Map<string, number>();

    for (const row of statement?.transactions || []) {
      const key = this.getStatementTransactionMatchKey(row);
      counts.set(key, (counts.get(key) || 0) + 1);
    }

    return counts;
  }

  private getMissingStatementTransactions(account: string | undefined, transactions: Transaction[], statementSummary: TransactionStatementPeriodSummary | null | undefined): MissingStatementTransaction[] {
    const statement = statementSummary?.statements?.find(row => row.accountNumber === account);

    if (!statement?.transactions?.length) {
      return [];
    }

    const ledgerCounts = new Map<string, number>();

    for (const transaction of transactions || []) {
      const key = this.getTransactionMatchKey(transaction);
      ledgerCounts.set(key, (ledgerCounts.get(key) || 0) + 1);
    }

    return statement.transactions
      .filter(row => this.matchesDirectionFilter(row.amount))
      .filter(row => {
        const key = this.getStatementTransactionMatchKey(row);
        const remainingMatches = ledgerCounts.get(key) || 0;

        if (remainingMatches > 0) {
          ledgerCounts.set(key, remainingMatches - 1);
          return false;
        }

        return true;
      })
      .map(row => ({
        ...row,
        sha1: this.createStatementTransactionSha1(account || '', row)
      }));
  }

  private getStatementTransactionMatchKey(row: BankStatementTransactionRow) {
    return `${this.getDateKey(row.date)}|${this.normaliseDescription(row.description)}|${row.amount || 0}`;
  }

  private getTransactionMatchKey(transaction: Transaction) {
    const original = transaction.source?.original || {};
    return `${this.getDateKey(transaction.date)}|${this.normaliseDescription(original.Description || original.description || transaction.description)}|${transaction.amount || 0}`;
  }

  private createTransactionFromStatementRow(row: MissingStatementTransaction) {
    return {
      date: this.parseBankDate(row.date),
      description: row.description,
      amount: row.amount,
      ignore: false,
      status: 'unknown',
      sorted: false,
      vat: 0,
      internal: false,
      proof: [],
      source: {
        account: this.activeAccount,
        original: {
          Date: row.date,
          Description: row.description,
          Debit: row.amount < 0 ? Math.abs(row.amount) / 100 : '',
          Credit: row.amount > 0 ? row.amount / 100 : '',
          Balance: row.balance / 100
        },
        sha1: row.sha1
      }
    };
  }

  private createStatementTransactionSha1(account: string, row: BankStatementTransactionRow) {
    return `csv-${account}-${this.getStatementTransactionMatchKey(row)}`;
  }

  private parseBankDate(date: string) {
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
      const [day, month, year] = date.split('/').map(value => +value);
      return new Date(year, month - 1, day);
    }

    return new Date(date);
  }

  private matchesDirectionFilter(amount: number) {
    if (this.transactionDirectionFilter === 'debits') {
      return amount < 0;
    }

    if (this.transactionDirectionFilter === 'credits') {
      return amount > 0;
    }

    return true;
  }

  private getDateKey(date: Date | string) {
    if (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
      const [day, month, year] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsedDate = new Date(date);
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private parseMoneyToCents(value: any) {
    if (value == null || value === '') {
      return 0;
    }

    const parsed = Number.parseFloat(String(value).replace(/[$,\s]/g, ''));

    if (Number.isNaN(parsed)) {
      return 0;
    }

    return Math.round(parsed * 100);
  }

  private normaliseDescription(description: any) {
    return String(description || '').trim();
  }

  private getRangeKey(startDate: Date | string, endDate: Date | string) {
    return `${new Date(startDate).toISOString().slice(0, 10)}_${new Date(endDate).toISOString().slice(0, 10)}`;
  }

  private getBookkeepingStartDate() {
    const configuredDate = this.appSettingsService.getBookkeepingStartDate();

    if (!configuredDate) {
      return null;
    }

    const startDate = new Date(configuredDate);

    if (Number.isNaN(startDate.getTime())) {
      return null;
    }

    startDate.setHours(0, 0, 0, 0);
    return startDate;
  }

  private clampDateRangeToBookkeepingStart(startDate: Date, endDate: Date) {
    const bookkeepingStartDate = this.getBookkeepingStartDate();

    if (bookkeepingStartDate && startDate < bookkeepingStartDate) {
      startDate = new Date(bookkeepingStartDate);
    }

    return {
      start: startDate,
      end: endDate
    };
  }

}

function setToMonday(date: Date): Date {
  const day = date.getDay() || 7;

  if (day !== 1) {
    date.setHours(-24 * (day - 1));
  }

  return date;
}
